import { Suspense } from 'react';
import Skeleton from 'react-loading-skeleton';
import { useSelector } from 'react-redux';
import { Route, Switch } from 'react-router-dom';
import { isEmpty } from 'lodash';
import CategoryCardsCarousel from 'sealink/react-components/category-cards-carousel';
import { hasReservation, price } from 'utils/helpers';
import { useCurrentBookingState } from 'utils/hooks/use_current_booking_state';
import RebuildCart from 'components/rebuild_cart';
import {
  getAmountToPay,
  getCurrentBooking,
  getSurchargeAmount,
} from 'redux/selectors/current_booking';
import AccommodationFlow from 'tenants/sealink/accommodation_flow';
import LoginStatus from 'tenants/sealink/auth/login_status';
import BookingDetails from 'tenants/sealink/booking_details';
import BookingLanding from 'tenants/sealink/booking_landing';
import BookingSummary from 'tenants/sealink/booking_summary';
import CheckOutForm from 'tenants/sealink/check_out_flow/checkout_form';
import NoActiveBooking from 'tenants/sealink/check_out_flow/no_active_booking';
import Receipt from 'tenants/sealink/check_out_flow/receipt';
import { useCleanup } from 'tenants/sealink/check_out_flow/receipt/use_cleanup';
import FerryFlow from 'tenants/sealink/ferry_flow';
import StickySummary from 'tenants/sealink/sticky_summary';
import { NewBookingRoute } from 'tenants/sealink/utils/routing';
import BookingStateCrmProvider from './booking_state_crm_provider';
import useScrollToTop from './use_scroll_to_top';

const { carouselTitle, carouselContent } =
  window.ecom.craftConfig?.receiptPageConfig || {};

export const MetaFlow = () => {
  useScrollToTop();

  const metaFlow = useSelector((state) => state.ui.sealink.metaFlow);

  const fullScreen = metaFlow.currentStep?.match(/^\/bookings\/accommodation/);
  const redirect = useSelector((state) => state.checkout?.redirect);
  const currentBooking = useSelector(getCurrentBooking);
  const { checkoutWarningMessage = '', checkoutWarningMessageStyle = 'info' } =
    useSelector((state) => state.sealink?.checkout_config || {});
  const { isBookingPayable, isEditing, isDraft } = useCurrentBookingState();

  const showNoActiveBooking =
    !currentBooking && metaFlow.currentStep?.match(/^\/bookings\/checkout\/?$/);

  const amountToPay = useSelector((state) => getAmountToPay(state));
  const surchargeAmount = useSelector((state) => getSurchargeAmount(state));
  const paymentAmountWithSurcharge = amountToPay + surchargeAmount;
  const hideLogin = window.ecom.craftConfig?.features?.loginSettings?.hideLogin;

  useCleanup();

  const renderRoutes = () => (
    <>
      <Suspense fallback={<Skeleton height={800} />}>
        <Switch>
          <NewBookingRoute path="/bookings/ferry" component={FerryFlow} />
          <Route path="/bookings/:bookingId?/ferry" component={FerryFlow} />
          <NewBookingRoute
            path="/bookings/accommodation"
            component={AccommodationFlow}
          />
          <Route path="/bookings/checkout/receipt" component={Receipt} />
          <Route
            path="/bookings/checkout"
            component={isBookingPayable ? CheckOutForm : NoActiveBooking}
          />
          <Route path="/bookings/rebuild" component={RebuildCart} />
          <Route path="/bookings/:booking_id" component={BookingDetails} />
          <NewBookingRoute path="/bookings" component={BookingLanding} />
        </Switch>
      </Suspense>
    </>
  );

  if (showNoActiveBooking) {
    return (
      <>
        <div className="container overflow-hidden my-2 my-lg-5 pb-10 pb-lg-0">
          <div className="row">
            <div className="col-lg-7">{renderRoutes()}</div>
          </div>
        </div>
      </>
    );
  }

  if (fullScreen) {
    return (
      <>
        <div className="d-flex flex-column flex-grow-1">{renderRoutes()}</div>
      </>
    );
  }

  return (
    <>
      <div className="container my-2 my-lg-5 pb-10 pb-lg-0">
        <div className="row">
          <div className="col-lg-7">
            {redirect === false &&
              metaFlow.currentStep === '/bookings/checkout' && (
                <div className="mb-2">
                  <h1>Checkout</h1>
                  {checkoutWarningMessage && (
                    <div
                      className={`mt-2 alert alert-${checkoutWarningMessageStyle}`}
                      role="alert"
                    >
                      {checkoutWarningMessage}
                    </div>
                  )}
                </div>
              )}
            {metaFlow.currentStep !== '/' && !hideLogin && (
              <div className="d-lg-none mb-3">
                <LoginStatus />
              </div>
            )}
            {renderRoutes()}
          </div>
          <div className="col-1 d-none d-lg-block vertical-divider" />
          <div className="col-lg-4 d-none d-lg-block">
            {!hideLogin && <LoginStatus />}
            <div className="mb-3" />
            {/*
             Hide the sidebar summary component when editing a non-draft booking
             The logic could be removed once the draft booking is released,
             only need to hide it on the receipt and booking summary pages
             */}
            {(!isEditing || isDraft) && (
              <>
                <h5 className="mb-3">Your trip summary</h5>
                <BookingSummary />
              </>
            )}
          </div>
        </div>
        {!isEditing &&
          !metaFlow.currentStep?.startsWith('/bookings/checkout/receipt') && (
            <StickySummary
              hasReservation={hasReservation(currentBooking)}
              reference={currentBooking?.reference}
              total={
                paymentAmountWithSurcharge
                  ? price(paymentAmountWithSurcharge)
                  : null
              }
            />
          )}
        {metaFlow.currentStep?.startsWith('/bookings/checkout/receipt') &&
          !isEmpty(carouselContent) && (
            <div className="carousel__mobile-container with-border mt-5 mt-lg-10 pt-5 pt-lg-8">
              {carouselTitle && (
                <h4 className="container-xl text-left text-md-center mb-4 mb-md-5">
                  {carouselTitle}
                </h4>
              )}
              <CategoryCardsCarousel categories={carouselContent} />
            </div>
          )}
      </div>
    </>
  );
};

export const MetaFlowWithCRM = () => {
  if (ecom.craftConfig?.features?.salesforce?.bookingUpdate) {
    return (
      <BookingStateCrmProvider>
        <MetaFlow />
      </BookingStateCrmProvider>
    );
  }
  return <MetaFlow />;
};
